import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../services/api';
import {
  FiHome,
  FiUsers,
  FiEye,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiFileText,
  FiAlertCircle
} from 'react-icons/fi';

interface StatCard {
  id: string;
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
    period: string;
  };
  icon: React.ComponentType<any>;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
  href?: string;
}

const OverviewStats: React.FC = () => {
  const [stats, setStats] = useState<StatCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStatistics();
  }, []);

  const fetchDashboardStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('ไม่พบ access token กรุณาเข้าสู่ระบบใหม่');
      }

      console.log('Fetching dashboard statistics from API...');

      
      const response = await axios.get(`${API_URL}/api/admin/dashboard/statistics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Dashboard statistics response:', response.data);

      if (response.data.success) {
        const data = response.data.data;

        // แปลงข้อมูลจาก API เป็น StatCard format
        const statsData: StatCard[] = [
          {
            id: 'companies',
            title: 'บริษัททั้งหมด',
            value: data.total_companies,
            change: {
              value: Math.abs(data.company_change_percent),
              type: data.company_change_percent > 0 ? 'increase' : data.company_change_percent < 0 ? 'decrease' : 'neutral',
              period: 'เดือนนี้'
            },
            icon: FiHome,
            color: 'blue',
            href: '/admin/companies'
          },
          {
            id: 'users',
            title: 'ผู้ใช้งาน',
            value: data.total_users,
            change: {
              value: Math.abs(data.user_change_percent),
              type: data.user_change_percent > 0 ? 'increase' : data.user_change_percent < 0 ? 'decrease' : 'neutral',
              period: 'เดือนนี้'
            },
            icon: FiUsers,
            color: 'green',
            href: '/admin/users'
          },
          {
            id: 'page_views',
            title: 'ผู้เข้าชมเว็บไซต์',
            value: data.page_views >= 1000 ? `${(data.page_views / 1000).toFixed(1)}K` : data.page_views,
            change: {
              value: Math.abs(data.page_views_change_percent),
              type: data.page_views_change_percent > 0 ? 'increase' : data.page_views_change_percent < 0 ? 'decrease' : 'neutral',
              period: 'เดือนนี้'
            },
            icon: FiEye,
            color: 'yellow'
          }
        ];

        setStats(statsData);
        console.log('Dashboard statistics loaded successfully:', statsData);
      } else {
        throw new Error('Failed to fetch dashboard statistics');
      }
    } catch (err: any) {
      console.error('Error fetching dashboard statistics:', err);
      setError(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color: StatCard['color']) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-500',
        lightBg: 'bg-blue-50',
        text: 'text-blue-600',
        lightText: 'text-blue-500'
      },
      green: {
        bg: 'bg-green-500',
        lightBg: 'bg-green-50',
        text: 'text-green-600',
        lightText: 'text-green-500'
      },
      yellow: {
        bg: 'bg-yellow-500',
        lightBg: 'bg-yellow-50',
        text: 'text-yellow-600',
        lightText: 'text-yellow-500'
      },
      red: {
        bg: 'bg-red-500',
        lightBg: 'bg-red-50',
        text: 'text-red-600',
        lightText: 'text-red-500'
      },
      purple: {
        bg: 'bg-purple-500',
        lightBg: 'bg-purple-50',
        text: 'text-purple-600',
        lightText: 'text-purple-500'
      },
      indigo: {
        bg: 'bg-indigo-500',
        lightBg: 'bg-indigo-50',
        text: 'text-indigo-600',
        lightText: 'text-indigo-500'
      }
    };
    return colorMap[color];
  };

  const getChangeIcon = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return <FiTrendingUp className="h-4 w-4" />;
      case 'decrease':
        return <FiTrendingDown className="h-4 w-4" />;
      default:
        return <FiMinus className="h-4 w-4" />;
    }
  };

  const getChangeColorClass = (type: 'increase' | 'decrease' | 'neutral') => {
    switch (type) {
      case 'increase':
        return 'text-green-600 bg-green-100';
      case 'decrease':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const renderStatCard = (stat: StatCard) => {
    const colors = getColorClasses(stat.color);
    const CardWrapper = stat.href ? 'a' : 'div';
    const cardProps = stat.href ? { href: stat.href } : {};

    return (
      <CardWrapper
        key={stat.id}
        {...cardProps}
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 ${
          stat.href ? 'cursor-pointer hover:border-gray-300' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">
              {stat.title}
            </p>
            <p className="text-3xl font-bold text-gray-900 mb-2">
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </p>

            {stat.change && (
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                getChangeColorClass(stat.change.type)
              }`}>
                {getChangeIcon(stat.change.type)}
                <span className="ml-1">
                  {stat.change.type === 'increase' ? '+' : stat.change.type === 'decrease' ? '-' : ''}
                  {Math.abs(stat.change.value)}% {stat.change.period}
                </span>
              </div>
            )}
          </div>

          <div className={`p-3 rounded-full ${colors.lightBg}`}>
            <stat.icon className={`h-8 w-8 ${colors.text}`} />
          </div>
        </div>
      </CardWrapper>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="mb-8">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">ภาพรวมระบบ</h2>
          <p className="text-sm text-gray-600">
            สถิติและข้อมูลสำคัญของระบบ Thai Security Directory
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="p-3 rounded-full bg-gray-200">
                  <div className="h-8 w-8"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="mb-8">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">ภาพรวมระบบ</h2>
          <p className="text-sm text-gray-600">
            สถิติและข้อมูลสำคัญของระบบ Thai Security Directory
          </p>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <FiAlertCircle className="h-6 w-6 text-red-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-1">เกิดข้อผิดพลาดในการโหลดข้อมูล</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={fetchDashboardStatistics}
              className="ml-4 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              ลองอีกครั้ง
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success State - Display Stats
  return (
    <div className="mb-8">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">ภาพรวมระบบ</h2>
        <p className="text-sm text-gray-600">
          สถิติและข้อมูลสำคัญของระบบ Thai Security Directory (Real-time data from API)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => renderStatCard(stat))}
      </div>
    </div>
  );
};

export default OverviewStats;